/**
 * AppShell — the frame every authenticated route renders inside.
 *
 * It owns the one piece of cross-component state the shell needs: whether the
 * slide-over navigation is open below `lg`. That state is dropped on every route
 * change (so a tapped link never leaves the panel covering the new page) and when
 * the viewport grows past `lg`, where the panel is replaced by the permanent rail
 * and its focus trap and scroll lock must not outlive it. Escape is handled by the
 * panel itself through the shared `useDismissable` contract in Sidebar.
 *
 * The skip link is the first child of the shell so it is the first thing a
 * keyboard user reaches; it targets the `<main>` below.
 */
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

export function AppShell() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const { pathname } = useLocation();

  const openSidebar = useCallback(() => setIsSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsSidebarOpen(false), []);

  // Close the slide-over when the route changes. This is React's documented
  // "adjust state when a prop changes" pattern rather than an effect: it runs
  // during render, so the sidebar never paints open on the new page first.
  const [lastPath, setLastPath] = useState(pathname);
  if (lastPath !== pathname) {
    setLastPath(pathname);
    if (isSidebarOpen) setIsSidebarOpen(false);
  }

  useEffect(() => {
    const query = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event: MediaQueryListEvent) => {
      if (event.matches) setIsSidebarOpen(false);
    };

    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return (
    <div className="min-h-screen bg-canvas">
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>

      <Sidebar isOpen={isSidebarOpen} onClose={closeSidebar} />

      {/* Offset for the fixed rail; below `lg` the rail is not in the layout. */}
      <div className="lg:pl-64">
        <Topbar isSidebarOpen={isSidebarOpen} onOpenSidebar={openSidebar} />

        <main id="main-content" tabIndex={-1} className="focus:outline-none">
          <div className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
